import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
  type ReactNode,
} from "react";

interface BaseFieldProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
}

const labelRow = (id: string, label?: string, required?: boolean, hint?: string): ReactNode =>
  label ? (
    <label
      htmlFor={id}
      style={{
        display: "block",
        fontSize: 13,
        fontWeight: 500,
        marginBottom: 6,
        color: "var(--fg)",
      }}
    >
      {label}
      {required ? <span style={{ color: "#FF2D1F", marginLeft: 4 }}>*</span> : null}
      {hint ? (
        <span style={{ color: "var(--fg-subtle)", fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
          {hint}
        </span>
      ) : null}
    </label>
  ) : null;

const controlStyle = (hasError: boolean) => ({
  width: "100%",
  background: "var(--bg)",
  color: "var(--fg)",
  border: `1px solid ${hasError ? "#FF2D1F" : "var(--border)"}`,
  borderRadius: 6,
  padding: "10px 12px",
  fontSize: 14,
  outline: "none",
});

const errorRow = (id: string, error?: string): ReactNode =>
  error ? (
    <p id={`${id}-error`} role="alert" style={{ color: "#FF2D1F", fontSize: 12, marginTop: 6 }}>
      {error}
    </p>
  ) : null;

export type TextFieldProps = BaseFieldProps & InputHTMLAttributes<HTMLInputElement>;

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hint, error, required, id, style, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <div>
      {labelRow(fieldId, label, required, hint)}
      <input
        ref={ref}
        id={fieldId}
        required={required}
        aria-invalid={!!error}
        aria-describedby={error ? `${fieldId}-error` : undefined}
        style={{ ...controlStyle(!!error), ...style }}
        {...rest}
      />
      {errorRow(fieldId, error)}
    </div>
  );
});

export type TextAreaFieldProps = BaseFieldProps & TextareaHTMLAttributes<HTMLTextAreaElement>;

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  function TextAreaField({ label, hint, error, required, id, style, ...rest }, ref) {
    const autoId = useId();
    const fieldId = id ?? autoId;
    return (
      <div>
        {labelRow(fieldId, label, required, hint)}
        <textarea
          ref={ref}
          id={fieldId}
          required={required}
          aria-invalid={!!error}
          aria-describedby={error ? `${fieldId}-error` : undefined}
          style={{ ...controlStyle(!!error), resize: "vertical", minHeight: 88, ...style }}
          {...rest}
        />
        {errorRow(fieldId, error)}
      </div>
    );
  },
);
